import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import buttonImage from '../buttonimage.webp';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { checkReadingLimit, handleDeleteReading } from '../utils/readingUtils';

export default function Relationships({ goBack, goHome, handleSaveProgress }) {
    // State for Essense Picker
    const [essenseCards, setEssenseCards] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [essensePickerVisible, setEssensePickerVisible] = useState(false);
    const [essenseSelectedCards, setEssenseSelectedCards] = useState([]);
    const [essenseClickedCardId, setEssenseClickedCardId] = useState(null);

    // State for FairRelating Picker
    const [fairRelatingCards, setFairRelatingCards] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [fairRelatingPickerVisible, setFairRelatingPickerVisible] = useState(false);
    const [fairRelatingSelectedCards, setFairRelatingSelectedCards] = useState([]);
    const [fairRelatingClickedCardId, setFairRelatingClickedCardId] = useState(null);

    // Shared state for both pickers
    const [savedReadings, setSavedReadings] = useState([]);
    const [saving, setSaving] = useState(false);
    const [essenseCardsLoaded, setEssenseCardsLoaded] = useState(false);
    const [fairRelatingCardsLoaded, setFairRelatingCardsLoaded] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [isLoadingPickedCards, setIsLoadingPickedCards] = useState(false);

    // New: Separate show/hide state for each picker
    const [showEssensePicker, setShowEssensePicker] = useState(false);
    const [showFairRelatingPicker, setShowFairRelatingPicker] = useState(false);

    const retrySupabaseQuery = React.useCallback(async (queryFn, retries = 3, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    console.warn(`Retrying query (attempt ${attempt})...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    }, []);

    const fetchEssenseCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions, card_number')
                    .eq('component', 'Essense')
            );
            if (error) throw error;
            setEssenseCards(data || []);
            setEssenseCardsLoaded(true);
        } catch (err) {
            setEssenseCards([]);
            setEssenseCardsLoaded(false);
            toast.error('Failed to load Essence cards.');
            console.error('Error fetching Essense cards:', err.message);
        }
    }, [retrySupabaseQuery]);

    const fetchFairRelatingCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions, card_number')
                    .eq('component', 'Relationships')
            );
            if (error) throw error;
            setFairRelatingCards(data || []);
            setFairRelatingCardsLoaded(true);
        } catch (err) {
            setFairRelatingCards([]);
            setFairRelatingCardsLoaded(false);
            toast.error('Failed to load FairRelating cards.');
            console.error('Error fetching FairRelating cards:', err.message);
        } finally {
            setLoading(false);
        }
    }, [retrySupabaseQuery]);

    const fetchSavedReadings = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .select('*')
                    .eq('user_id', user.id)
                    .in('component', ['Essense-Relationships', 'FairRelating-Relationships'])
                    .order('created_at', { ascending: false })
            );
            if (error) throw error;
            setSavedReadings(data || []);
        } catch (err) {
            console.error('Error fetching saved readings:', err.message);
        }
    }, [retrySupabaseQuery]);

    useEffect(() => {
        fetchEssenseCards();
        fetchFairRelatingCards();
        fetchSavedReadings();
    }, [fetchEssenseCards, fetchFairRelatingCards, fetchSavedReadings]);

    useEffect(() => {
        if (essenseSelectedCards.length > 0 || fairRelatingSelectedCards.length > 0) {
            setIsLoadingPickedCards(true);
            const timer = setTimeout(() => setIsLoadingPickedCards(false), 500);
            return () => clearTimeout(timer);
        }
    }, [essenseSelectedCards, fairRelatingSelectedCards]);

    const handleEssenseCardClick = (card) => {
        if (essenseSelectedCards.length < 6 && !essenseSelectedCards.some(c => c.id === card.id)) {
            setEssenseSelectedCards([...essenseSelectedCards, card]);
            setEssenseClickedCardId(card.id);
            setTimeout(() => setEssenseClickedCardId(null), 200);
        }
    };

    const handleFairRelatingCardClick = (card) => {
        if (fairRelatingSelectedCards.length < 6 && !fairRelatingSelectedCards.some(c => c.id === card.id)) {
            setFairRelatingSelectedCards([...fairRelatingSelectedCards, card]);
            setFairRelatingClickedCardId(card.id);
            setTimeout(() => setFairRelatingClickedCardId(null), 200);
        }
    };

    const handleSaveEssenseReading = async () => {
        if (essenseSelectedCards.length !== 6) {
            toast.error('Please select exactly 6 cards to save your Essence reading.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                toast.error('User not logged in. Please log in to save your reading.');
                return;
            }

            const canSave = await checkReadingLimit(user.id, 'Essense-Relationships', retrySupabaseQuery, supabase);
            if (!canSave) {
                toast.error('You have reached the limit of 10 saved Essence readings. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = essenseSelectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.Relationships?.[position] || card.descriptions || 'No description available';
                return acc;
            }, {});

            const readingData = {
                user_id: user.id,
                component: 'Essense-Relationships',
                cards: essenseSelectedCards.map((card) => card.id),
                descriptions,
            };

            const { error: insertError } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert(readingData)
            );

            if (insertError) {
                console.error('Supabase insert error:', insertError.message);
                throw insertError;
            }

            toast.success('Essence reading saved successfully!');
            setEssenseSelectedCards([]);
            setEssensePickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            toast.error('Failed to save Essence reading. Please try again.');
            console.error('Error saving Essense reading:', err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleSaveFairRelatingReading = async () => {
        if (fairRelatingSelectedCards.length !== 6) {
            toast.error('Please select exactly 6 cards to save your FairRelating reading.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                toast.error('User not logged in. Please log in to save your reading.');
                return;
            }

            const canSave = await checkReadingLimit(user.id, 'FairRelating-Relationships', retrySupabaseQuery, supabase);
            if (!canSave) {
                toast.error('You have reached the limit of 10 saved FairRelating readings. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = fairRelatingSelectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.Relationships?.[position] || card.descriptions || 'No description available';
                return acc;
            }, {});

            const readingData = {
                user_id: user.id,
                component: 'FairRelating-Relationships',
                cards: fairRelatingSelectedCards.map((card) => card.id),
                descriptions,
            };

            const { error: insertError } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert(readingData)
            );

            if (insertError) {
                console.error('Supabase insert error:', insertError.message);
                throw insertError;
            }

            toast.success('FairRelating reading saved successfully!');
            setFairRelatingSelectedCards([]);
            setFairRelatingPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            toast.error('Failed to save FairRelating reading. Please try again.');
            console.error('Error saving FairRelating reading:', err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleLoadReading = (reading) => {
        if (reading.component === 'Essense-Relationships') {
            if (!essenseCardsLoaded || essenseCards.length === 0) {
                toast.info('Loading Essence card data. Please wait a moment and try again.');
                return;
            }

            const cardsById = essenseCards.reduce((acc, card) => {
                acc[card.id] = card;
                return acc;
            }, {});

            const loadedCards = reading.cards.map((cardId) => {
                const card = cardsById[cardId];
                if (!card) {
                    console.warn(`Card ID ${cardId} not found while loading saved Essence reading.`);
                    return null;
                }
                return {
                    ...card,
                    descriptions: card.descriptions || 'No description available',
                };
            }).filter(Boolean);

            if (loadedCards.length !== reading.cards.length) {
                toast.error('Some Essence cards could not be loaded for this saved reading. Please refresh and try again.');
            }

            setEssenseSelectedCards(loadedCards);
            setEssensePickerVisible(false);
            setFairRelatingPickerVisible(false);
        } else if (reading.component === 'FairRelating-Relationships') {
            if (!fairRelatingCardsLoaded || fairRelatingCards.length === 0) {
                toast.info('Loading FairRelating card data. Please wait a moment and try again.');
                return;
            }

            const cardsById = fairRelatingCards.reduce((acc, card) => {
                acc[card.id] = card;
                return acc;
            }, {});

            const loadedCards = reading.cards.map((cardId) => {
                const card = cardsById[cardId];
                if (!card) {
                    console.warn(`Card ID ${cardId} not found while loading saved FairRelating reading.`);
                    return null;
                }
                return {
                    ...card,
                    descriptions: card.descriptions || 'No description available',
                };
            }).filter(Boolean);

            if (loadedCards.length !== reading.cards.length) {
                toast.error('Some FairRelating cards could not be loaded for this saved reading. Please refresh and try again.');
            }

            setFairRelatingSelectedCards(loadedCards);
            setFairRelatingPickerVisible(false);
            setEssensePickerVisible(false);
        }
    };

    // Shuffle helper
    const shuffleArray = (array) => {
        const arr = array.slice();
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    // Update: Only Start button resets cards, Hide just hides picker
    const handleStartEssenseReading = () => {
        setEssenseSelectedCards([]);
        setEssenseCards(shuffleArray([...essenseCards]));
        setShowEssensePicker(true);
    };
    const handleHideEssensePicker = () => {
        setShowEssensePicker(false);
    };
    // eslint-disable-next-line no-unused-vars
    const handleShowEssensePicker = () => {
        setShowEssensePicker(true);
    };

    const handleStartFairRelatingReading = () => {
        setFairRelatingSelectedCards([]);
        setFairRelatingCards(shuffleArray([...fairRelatingCards]));
        setShowFairRelatingPicker(true);
    };
    const handleHideFairRelatingPicker = () => {
        setShowFairRelatingPicker(false);
    };
    // eslint-disable-next-line no-unused-vars
    const handleShowFairRelatingPicker = () => {
        setShowFairRelatingPicker(true);
    };

    // Helper to filter out back images from card sets
    const isNotBackImage = (card, setName) => {
        if (!card.image_path) return true;
        const lower = card.image_path.toLowerCase();
        if (setName === 'Essense') {
            return !lower.includes('essense back');
        }
        if (setName === 'FairRelating') {
            return !lower.includes('fairrelating back');
        }
        return true;
    };

    return (
        <div
            style={{
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '16px',
            }}
        >
            <ToastContainer />
            <h2
                style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '32px',
                    fontWeight: 'bold',
                    color: '#4B5563',
                    margin: '32px 0 24px 0',
                    textAlign: 'center',
                }}
            >
                Relationships
            </h2>

            {/* Session Helper Cards Row */}
            <div
                className="relationships-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• Life is really about relationship</div>
                        <div>• An aversion to others can be a coping mechanism</div>
                        <div>• Relating is a skill that can be improved upon</div>
                        <div>• Relationships give us our best mirrors</div>
                        <div>• We inherit relating patterns from our caregivers </div>
                        <div>• There is a way to relate with higher standards to ensure fairness </div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. Notice what expectations you have for yourself, others, or life in this situation. Are they realistic, helpful, or limiting?</div>
                        <div>2. Reflect on how unmet expectations may be affecting your emotions or actions. What would it feel like to release or adjust these expectations?</div>
                        <div>3. Consider setting intentions instead of expectations. How can you approach this situation with openness and curiosity?</div>
                        <div>4. Is there something you have learned you can do better in your relationships?</div>
                        <div>5. Has understanding been the goal between you and others you relate with?</div>
                        <div>6. Do you avoid uncomfortable conversations that would give clarity in your relationships?</div>
                    </div>
                </div>
            </div>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    width: '100%',
                    maxWidth: '700px',
                    alignItems: 'center',
                }}
            >
                {/* Card 1 */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: 0,
                            textAlign: 'center',
                        }}
                    >
                        How we relate to other people is learned behavior from what was modeled to us.  We can also practice methods that we discover in order to improve our relationships when we know something is not working.  Conflict resolution is an important skill in order to maintain relationships, but if it was never modeled or taught to us we may not have that skill.  It is valuable to know how to repair relationships with others.  It is a skill that can be learned as well.  I have learned that having expectations of others can feel like an energetic burden.  It does not feel good to be expected of by others.  Instead we can make agreements with others about how we are willing to show up in the relationship.  This will help eliminate the disappointment that can happen when we expect of each other.  <br />  Sometimes we can decide what a relationship is meant to be when our soul has decided it was meant to be something else entirely.  We can see how the energy of different people works together when we look at the astrology charts of each person and how the energy they are working with interacts.   It is important to be open to the outcome of the relationship as it unfolds instead of deciding ahead of time what it must be.  Even in a family relationship the other will not neccessarily fit our expectations.  To love another is to allow them to be free. To love another is to not put conditions on the love we give.  <br />  Of course sometimes we find that we are not compatible with others when we allow them to be who they truly are.  Also, sometimes when others are acting from a place of being unhealed they can have behaviors that make it impossible for us to remain in their lives.  It is possible to love others from a distance, and there may be a grieving process for what we wished the relationship could be.  <br /> Even when a relationship does not last for long it can have valuable life lessons, reflections, insights and inspirations to offer us. This is why it is important to follow our hearts, but be open to the outcome we get.  We don't want to miss out on any important experiences that our soul has set up for us. 
                    </p>
                </div>
                {/* Card 2 */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: 0,
                            textAlign: 'center',
                        }}
                    >
                        Energy movement can also be used to improve relationships.  I have used green love energy from my heart to help ease tensions. (just by imagining the energy radiating from my heart) and I have also used meditation to change the past circumstances, which also changed the present. <br />Consider how you communicate your needs and boundaries. Are there areas where you can listen more deeply or express yourself more clearly? Consider setting intentions instead of expectations. How can you approach this situation with openness and curiosity?
                    </p>
                </div>
                {/* Card 3 */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        minHeight: '120px',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        borderRadius: '10px',
                        border: '2px solid #4B5563',
                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                        padding: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        margin: '0 auto',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            color: '#4B5563',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            margin: 0,
                            textAlign: 'center',
                        }}
                    >
                        

Below you will find two Oracle card sets that can be used to help you with information to reflect on.  The Essence Reading just offers some energy concepts to consider, while the Fair Relating Reading is a card deck I designed to help myself and others while in the transition toward a more balanced way of relating. <br /> This Fair Relating deck reflects the higher standards I had adopted for myself after waking up to dysfuntional and co-dependant relating patterns I had been in prior. Eventually we can embody the concepts in the Fair Relating Deck and will not need the guidance it offers because it comes naturally.

                    </p>
                </div>
                {/* New Card with Compassion meme image */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <img
                        src="/images/compassionmeme.webp"
                        alt="Compassion Meme"
                        style={{
                            width: '100%',
                            maxWidth: '360px',
                            height: 'auto',
                            marginBottom: '12px',
                            borderRadius: '8px',
                            border: '1px solid #4B5563',
                            objectFit: 'contain',
                            display: 'block',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}
                    />
                </div>
                {/* Existing Invalidation Meme card */}
                <div
                    style={{
                        width: '100%',
                        maxWidth: '600px',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '16px',
                        textAlign: 'center',
                        backgroundImage: `url(${cardBackgroundImage})`,
                        backgroundSize: 'cover',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '0 auto',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <img
                        src="/images/invalidation%20Meme.webp"
                        alt="Invalidation Meme"
                        style={{
                            width: '100%',
                            maxWidth: '360px',
                            height: 'auto',
                            marginBottom: '12px',
                            borderRadius: '8px',
                            border: '1px solid #4B5563',
                            objectFit: 'contain',
                            display: 'block',
                            marginLeft: 'auto',
                            marginRight: 'auto'
                        }}
                    />
                </div>
            </div>

            {/* Start/Hide Essence Reading Button */}
            <button
                style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: 'auto',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    margin: '16px auto',
                }}
                onClick={
                    showEssensePicker
                        ? handleHideEssensePicker
                        : handleStartEssenseReading
                }
            >
                {showEssensePicker
                    ? 'Hide Essence Picker'
                    : 'Start Essence Reading'}
            </button>

            {/* Essence Card Picker */}
            {showEssensePicker && (
                <div
                    className="essense-card-picker"
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '8px 16px',
                        overflowX: 'hidden',
                    }}
                >
                    {essenseCards.filter(card => isNotBackImage(card, 'Essense')).reduce((rows, card, index) => {
                        // For mobile, use 20 per row; for browser, keep 71 per row
                        const isMobile = window.innerWidth <= 600;
                        const perRow = isMobile ? 20 : 71;
                        const rowIndex = Math.floor(index / perRow);
                        if (!rows[rowIndex]) rows[rowIndex] = [];
                        rows[rowIndex].push(card);
                        return rows;
                    }, []).map((row, rowIndex) => (
                        <div
                            key={rowIndex}
                            className="essense-card-row"
                            style={{
                                display: 'flex',
                                flex: '0 0 100%',
                                marginTop: rowIndex === 0 ? 0 : '-10px',
                                overflow: 'hidden',
                            }}
                        >
                            {row.map((card, index) => (
                                <div
                                    key={card.id}
                                    className="essense-card"
                                    style={{
                                        width: '120px',
                                        height: '164px',
                                        cursor: 'pointer',
                                        marginLeft: index === 0 ? 0 : '-100px',
                                        boxShadow: essenseClickedCardId === card.id ? '0 0 10px rgba(0, 255, 0, 0.5)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        transform: essenseClickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                        zIndex: rowIndex * (window.innerWidth <= 600 ? 20 : 71) + index,
                                        backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/Essense/Essense%20Back002.webp)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        borderRadius: '8px',
                                    }}
                                    onClick={() => handleEssenseCardClick(card)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {/* Essence Selected Cards Display */}
            {essenseSelectedCards.filter(card => isNotBackImage(card, 'Essense')).length > 0 && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridTemplateRows: 'repeat(2, 1fr)',
                    gap: '8px',
                    marginTop: '16px',
                    background: 'white',
                    border: '2px solid black',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    maxWidth: '600px',
                    margin: '16px auto',
                    width: '100%',
                }}>
                    {essenseSelectedCards
                        .filter(card => isNotBackImage(card, 'Essense'))
                        .map((card, index) => {
                            const labels = [
                                'How you feel about yourself',
                                'How you see the other',
                                'What you want most right now',
                                'What to let go of',
                                'Heart of the matter',
                                'The likely outcome or advice'
                            ];
                            return (
                                <div key={card.id} style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                                }}>
                                    <img
                                        src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                        alt={card.title}
                                        style={{ width: '150px', height: '150px', objectFit: 'contain' }}
                                        onError={() => console.error(`Failed to load card image: ${card.image_path}`)}
                                    />
                                    <p style={{
                                        fontFamily: 'Times New Roman, Times, serif',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        marginTop: '8px',
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                        maxWidth: '150px',
                                    }}>
                                        {labels[index] || 'No description available'}
                                    </p>
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Save Essence Reading Button */}
            {essenseSelectedCards.length === 6 && (
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                    }}
                    onClick={handleSaveEssenseReading}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Essence Reading'}
                </button>
            )}

            {/* Start/Hide FairRelating Reading Button */}
            <button
                style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: 'auto',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    margin: '16px auto',
                }}
                onClick={
                    showFairRelatingPicker
                        ? handleHideFairRelatingPicker
                        : handleStartFairRelatingReading
                }
            >
                {showFairRelatingPicker
                    ? 'Hide Fair Relating Picker'
                    : 'Start Fair Relating Reading'}
            </button>

            {/* FairRelating Card Picker */}
            {showFairRelatingPicker && (
                <div
                    className="fairrelating-card-picker"
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-start',
                        maxWidth: '1200px',
                        margin: '0 auto',
                        padding: '8px 16px',
                        overflowX: 'hidden',
                    }}
                >
                    {fairRelatingCards.filter(card => isNotBackImage(card, 'FairRelating')).reduce((rows, card, index) => {
                        // For mobile, use 20 per row; for browser, keep 71 per row
                        const isMobile = window.innerWidth <= 600;
                        const perRow = isMobile ? 20 : 71;
                        const rowIndex = Math.floor(index / perRow);
                        if (!rows[rowIndex]) rows[rowIndex] = [];
                        rows[rowIndex].push(card);
                        return rows;
                    }, []).map((row, rowIndex) => (
                        <div
                            key={rowIndex}
                            className="fairrelating-card-row"
                            style={{
                                display: 'flex',
                                flex: '0 0 100%',
                                marginTop: rowIndex === 0 ? 0 : '-10px',
                                overflow: 'hidden',
                            }}
                        >
                            {row.map((card, index) => (
                                <div
                                    key={card.id}
                                    className="fairrelating-card"
                                    style={{
                                        width: '120px',
                                        height: '100px',
                                        cursor: 'pointer',
                                        marginLeft: index === 0 ? 0 : '-100px',
                                        boxShadow: fairRelatingClickedCardId === card.id ? '0 0 10px rgba(0, 255, 0, 0.5)' : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        transform: fairRelatingClickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                        zIndex: rowIndex * (window.innerWidth <= 600 ? 20 : 71) + index,
                                        backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/FairRelating/FairRelating%20Back001.webp)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        borderRadius: '8px',
                                    }}
                                    onClick={() => handleFairRelatingCardClick(card)}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            )}

            {/* FairRelating Selected Cards Display */}
            {fairRelatingSelectedCards.filter(card => isNotBackImage(card, 'FairRelating')).length > 0 && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gridTemplateRows: 'repeat(2, 1fr)',
                    gap: '8px',
                    marginTop: '16px',
                    background: 'white',
                    border: '2px solid black',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    padding: '12px',
                    borderRadius: '8px',
                    maxWidth: '600px',
                    margin: '16px auto',
                    width: '100%',
                }}>
                    {fairRelatingSelectedCards
                        .filter(card => isNotBackImage(card, 'FairRelating'))
                        .map((card, index) => {
                            const labels = [
                                'What needs to be honored',
                                'How can I love myself more?',
                                'Area to explore',
                                'Room for improvement',
                                'Present challenge',
                                'Summary'
                            ];
                            return (
                                <div key={card.id} style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                                }}>
                                    <img
                                        src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                        alt={card.title}
                                        style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                        onError={() => console.error(`Failed to load card image: ${card.image_path}`)}
                                    />
                                    <p style={{
                                        fontFamily: 'Times New Roman, Times, serif',
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        marginTop: '8px',
                                        textAlign: 'center',
                                        wordWrap: 'break-word',
                                        maxWidth: '100px',
                                    }}>
                                        {labels[index] || 'No description available'}
                                    </p>
                                </div>
                            );
                        })}
                </div>
            )}

            {/* Save FairRelating Reading Button */}
            {fairRelatingSelectedCards.length === 6 && (
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: 'auto',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                    }}
                    onClick={handleSaveFairRelatingReading}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save FairRelating Reading'}
                </button>
            )}

            {/* Navigation Buttons */}
            <div
                className="relationships-nav-btn-row"
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '16px',
                    marginTop: '56px',
                    marginBottom: '20px',
                }}
            >
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={goBack}
                >
                    Back
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={goHome}
                >
                    Home
                </button>
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    }}
                    onClick={handleSaveProgress}
                >
                    Save
                </button>
            </div>
            <style>
                {`
                @media (max-width: 600px) {
                    .relationships-nav-btn-row {
                        flex-wrap: nowrap;
                        gap: 4px !important;
                        margin-top: 24px !important;
                        margin-bottom: 8px !important;
                    }
                    .relationships-nav-btn-row button {
                        min-width: 0 !important;
                        width: 32vw !important;
                        max-width: 110px !important;
                        font-size: 13px !important;
                        padding: 8px 0 !important;
                    }
                    .essense-card-picker, .fairrelating-card-picker {
                        max-width: 100vw !important;
                        padding: 0 2vw !important;
                    }
                    .essense-card-row, .fairrelating-card-row {
                        display: flex;
                        flex-direction: row;
                        width: 100vw;
                        overflow-x: hidden;
                        margin-bottom: 0;
                        margin-top: 0;
                    }
                    .essense-card, .fairrelating-card {
                        width: 36px !important;
                        height: 48px !important;
                        margin-left: -24px !important;
                        border-radius: 6px !important;
                    }
                    .essense-card:first-child, .fairrelating-card:first-child {
                        margin-left: 0 !important;
                    }
                }
                `}
            </style>
            {/* Saved Readings */}
            {savedReadings.length > 0 && (
                <div style={{
                    marginTop: '32px',
                    width: '100%',
                    maxWidth: '600px',
                    padding: '8px',
                }}>
                    <h4 style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#4B5563',
                        textAlign: 'center',
                        marginBottom: '16px',
                    }}>
                        Saved Readings
                    </h4>
                    {savedReadings.map((reading) => (
                        <div
                            key={reading.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                border: '1px solid black',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '14px',
                                color: '#4B5563',
                            }}
                        >
                            <span onClick={() => handleLoadReading(reading)}>
                                {reading.component === 'Essense-Relationships' ? 'Essence Reading: ' : 'FairRelating Reading: '}
                                {new Date(reading.created_at).toLocaleString()}
                            </span>
                            <span
                                style={{
                                    color: 'red',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteReading(reading.id, fetchSavedReadings, retrySupabaseQuery, supabase);
                                }}
                            >
                                -delete
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}